"use client";

/**
 * TelemetryStrip — the live-instrument strip from
 * `docs/Fable UiUx review/wtm-components-telemetry-galton-anchor.html`
 * (Component 01), wired to REAL data.
 *
 * Data sources:
 *  - UTC clock + next-cycle countdown (next Sunday 09:00 UTC, the weekly
 *    Compass cycle anchor) — computed client-side from the system clock.
 *  - Counters — `GET /api/public/telemetry` (the committed endpoint).
 *    The endpoint returns only metrics it could actually compute
 *    (`analyzerRunsThisWeek`, `briefsDelivered`, `lastCycleDurationMs`,
 *    `weekStartUtc`, `asOf`) and OMITS anything it can't. This component
 *    mirrors that honesty rule: a cell renders only when its metric is
 *    present in the response — never a fabricated number, never a
 *    placeholder that looks like data.
 *
 * Loading / fetch-fail: the numeric cells (and the OPERATIONAL status,
 * which is only claimable after a successful fetch) are hidden; the
 * clock + countdown stay, so the strip degrades to a real instrument
 * rather than an empty error.
 */

import { useEffect, useState } from "react";
import { useCountUp } from "@/lib/motion";
import { cn } from "@/lib/utils/cn";
import { MONO, LINE } from "./shared";

export interface TelemetryStripProps {
  /** Override the telemetry endpoint (default `/api/public/telemetry`). */
  endpoint?: string;
  /** Render the hairline footer row (pipeline + sources). Default true. */
  showFooter?: boolean;
  className?: string;
}

interface TelemetryPayload {
  asOf: string;
  weekStartUtc?: string;
  analyzerRunsThisWeek?: number;
  briefsDelivered?: number;
  lastCycleDurationMs?: number;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** "11m 42s" / "1h 04m" / "42s" from a millisecond duration. */
function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${pad(m)}m`;
  if (m > 0) return `${m}m ${pad(s)}s`;
  return `${s}s`;
}

function utcClockString(now: Date): string {
  return `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())} UTC`;
}

/** Countdown to the next Sunday 09:00 UTC (the weekly cycle start). */
function countdownString(now: Date): string {
  const next = new Date(now);
  next.setUTCDate(now.getUTCDate() + ((7 - now.getUTCDay()) % 7));
  next.setUTCHours(9, 0, 0, 0);
  if (next.getTime() <= now.getTime()) next.setUTCDate(next.getUTCDate() + 7);
  let s = Math.floor((next.getTime() - now.getTime()) / 1000);
  const d = Math.floor(s / 86400);
  s -= d * 86400;
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  return `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`;
}

function Cell({
  label,
  children,
  valueClassName,
}: {
  label: string;
  children: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div
      className="min-w-[150px] flex-1 border-r px-5 py-3.5 last:border-r-0"
      style={{ borderColor: LINE }}
    >
      <div className="text-[0.58rem] uppercase tracking-[0.14em] text-[#64748B]">
        {label}
      </div>
      <div className={cn("mt-[5px] text-[0.92rem] tracking-[0.02em] text-[#F1F5F9]", valueClassName)}>
        {children}
      </div>
    </div>
  );
}

export function TelemetryStrip({
  endpoint = "/api/public/telemetry",
  showFooter = true,
  className,
}: TelemetryStripProps) {
  // ── clock + countdown (client-only; "—" until hydrated, no SSR mismatch)
  const [clock, setClock] = useState<{ utc: string; countdown: string } | null>(null);
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setClock({ utc: utcClockString(now), countdown: countdownString(now) });
    };
    tick();
    // The ticking clock is information, not decoration — it keeps updating
    // under prefers-reduced-motion (a frozen wrong time would be worse).
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  // ── counters from the committed public endpoint
  const [data, setData] = useState<TelemetryPayload | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(endpoint, { signal: ctrl.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`telemetry ${res.status}`);
        return res.json() as Promise<TelemetryPayload>;
      })
      .then((json) => setData(json))
      .catch((err: unknown) => {
        if ((err as Error)?.name !== "AbortError") setFailed(true);
      });
    return () => ctrl.abort();
  }, [endpoint]);

  // Count-ups (hooks are unconditional; cells render only when real).
  const hasRuns = typeof data?.analyzerRunsThisWeek === "number";
  const hasBriefs = typeof data?.briefsDelivered === "number";
  const runs = useCountUp(data?.analyzerRunsThisWeek ?? 0, { start: hasRuns });
  const briefs = useCountUp(data?.briefsDelivered ?? 0, { start: hasBriefs });
  const hasCycle = typeof data?.lastCycleDurationMs === "number";
  // "OPERATIONAL" is only claimable when the endpoint returned at least one
  // real metric — a DB-down response carries only `asOf`.
  const operational = !failed && (hasRuns || hasBriefs || hasCycle);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[14px] border bg-[#0A101F] shadow-[0_24px_60px_-24px_rgba(0,0,0,.55)]",
        className,
      )}
      style={{ borderColor: LINE }}
      aria-label="Live Compass telemetry"
    >
      <div className="flex flex-wrap items-stretch font-tabular" style={{ fontFamily: MONO }}>
        {/* Status — only claim OPERATIONAL when real metrics came back. */}
        {operational && (
          <Cell label="System status" valueClassName="text-cgreen">
            <span
              aria-hidden
              className="mr-[7px] inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-cgreen motion-reduce:animate-none"
            />
            OPERATIONAL
          </Cell>
        )}
        <Cell label="UTC clock">{clock?.utc ?? "-"}</Cell>
        <Cell label="Next cycle · Sun 7:00pm Syd (09:00 UTC)" valueClassName="text-gold">
          {clock?.countdown ?? "-"}
        </Cell>
        {hasCycle && (
          <Cell label="Last cycle">
            SUN 7:00PM SYD <span className="text-[0.7rem] text-[#64748B]">· {formatDuration(data!.lastCycleDurationMs!)}</span>
          </Cell>
        )}
        {hasRuns && (
          <Cell label="Analyzer runs this week">
            {Math.round(runs).toLocaleString("en-US")}
          </Cell>
        )}
        {hasBriefs && (
          <Cell label="Briefs delivered">
            {Math.round(briefs).toLocaleString("en-US")}
          </Cell>
        )}
      </div>
      {showFooter && (
        <div
          className="flex flex-wrap justify-between gap-2 border-t px-5 py-[9px] font-tabular text-[0.6rem] tracking-[0.1em] text-[#64748B]"
          style={{ fontFamily: MONO, borderColor: LINE }}
        >
          <span>PIPELINE · 7 ANALYZERS · ADVERSARIAL + DEBATE + SYNTHESIS</span>
          <span>SOURCES · FMP · SEC EDGAR · POLYGON · FRED</span>
          {data?.asOf && (
            <span>
              COUNTERS AS OF{" "}
              {utcClockString(new Date(data.asOf)).replace(" UTC", "")} UTC
            </span>
          )}
        </div>
      )}
    </div>
  );
}
